import { useEffect, useMemo } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Trophy, PoundSterling, Mail, FileText, LogOut, HeartHandshake, AlertTriangle, Home, Users, Megaphone, Gift, Dice5 } from 'lucide-react';

const LINKS = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/homepage', label: 'Homepage', icon: Home },
  { to: '/admin/competitions', label: 'Competitions', icon: PoundSterling },
  { to: '/admin/winners', label: 'Winners', icon: Trophy },
  { to: '/admin/draws', label: 'Draws', icon: Dice5 },
  { to: '/admin/postal', label: 'Postal entries', icon: FileText },
  { to: '/admin/waitlist', label: 'Waitlist', icon: Mail },
  { to: '/admin/charity', label: 'Charity log', icon: HeartHandshake },
  { to: '/admin/customers', label: 'Customers', icon: Users },
  { to: '/admin/broadcast', label: 'Broadcast', icon: Megaphone },
  { to: '/admin/partner-codes', label: 'Partner codes', icon: Gift },
];

// Detect whether we're on preview vs production based on the browser's own
// hostname. Anything that isn't the live domain (thepadeldraw.co.uk or its
// www variant) is treated as preview so we can warn the admin loudly that
// changes here DO NOT reach live customers.
const isProductionHost = () => {
  if (typeof window === 'undefined') return true; // SSR fallback
  const h = window.location.hostname.toLowerCase();
  return h === 'thepadeldraw.co.uk' || h === 'www.thepadeldraw.co.uk';
};

const AdminLayout = () => {
  const nav = useNavigate();
  useEffect(() => {
    if (!localStorage.getItem('pd_admin_token')) nav('/admin/login');
  }, [nav]);
  const signOut = () => { localStorage.removeItem('pd_admin_token'); nav('/admin/login'); };

  // Memoised so the check runs once per mount, not on every render.
  const onPreview = useMemo(() => !isProductionHost(), []);
  const currentHost = useMemo(() => (typeof window !== 'undefined' ? window.location.hostname : ''), []);

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[240px_1fr] bg-[color:var(--pd-bg)]" data-testid="admin-layout">
      <aside className="border-r border-pd bg-surface p-4 md:min-h-screen">
        <div className="font-display text-xl text-[color:var(--pd-accent-yellow)]">The Padel Draw</div>
        <div className="font-mono text-[10px] text-muted-sage uppercase">Admin</div>
        <nav className="mt-6 flex md:flex-col gap-1 flex-wrap" data-testid="admin-nav">
          {LINKS.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isActive ? 'bg-[color:var(--pd-accent-yellow)]/10 text-[color:var(--pd-accent-yellow)]' : 'text-muted-sage hover:text-[color:var(--pd-text)]'}`}
                data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              >
                <Icon size={16} />{l.label}
              </NavLink>
            );
          })}
        </nav>
        <button onClick={signOut} className="mt-6 flex items-center gap-2 px-3 py-2 text-sm text-muted-sage hover:text-[color:var(--pd-text)]" data-testid="admin-signout"><LogOut size={16}/> Sign out</button>
      </aside>
      <main className="p-6 md:p-10">
        {/* Environment banner: strong visual warning when the admin is NOT on
            the live production host. Prevents the "I made changes but the live
            site doesn't update" mistake we hit on 2026-07-29. */}
        {onPreview ? (
          <div
            className="mb-6 rounded-xl border border-[color:var(--pd-danger)] bg-[color:var(--pd-danger)]/10 px-4 py-3 flex items-start gap-3"
            role="alert"
            data-testid="admin-preview-banner"
          >
            <AlertTriangle size={18} className="text-[color:var(--pd-danger)] mt-0.5 shrink-0" />
            <div className="text-sm leading-relaxed">
              <div className="text-[color:var(--pd-text)] font-medium">
                You are on the PREVIEW environment ({currentHost}).
              </div>
              <div className="text-muted-sage">
                Any changes here — competitions, featured toggles, draws, winners — do <span className="text-[color:var(--pd-text)]">not</span> affect the live site.
                To manage the live site, log out and go to{' '}
                <a
                  href="https://thepadeldraw.co.uk/admin/login"
                  className="text-[color:var(--pd-accent-yellow)] underline hover:no-underline"
                  data-testid="admin-preview-banner-live-link"
                >
                  thepadeldraw.co.uk/admin/login
                </a>.
              </div>
            </div>
          </div>
        ) : (
          <div
            className="mb-4 rounded-lg border border-[color:var(--pd-accent-yellow)]/30 bg-[color:var(--pd-accent-yellow)]/5 px-3 py-2 text-xs text-muted-sage inline-flex items-center gap-2"
            data-testid="admin-live-banner"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--pd-accent-yellow)]" aria-hidden />
            You are on the LIVE site — changes here affect real customers.
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
};
export default AdminLayout;
